import React, { useLayoutEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Save } from 'lucide-react';

const STEPS = {
    ENTRY_TYPE: 'step_1_entry_type',
    TEMPLATE: 'step_3_template',
    DETAILS: 'step_4_details',
    PREVIEW: 'step_5_preview'
};

const BookkeepingNavigationBar = ({
    activeStep,
    onStepChange,
    operationInProgress,
    canProceedToNext,
    canGoBack,
    getNextStep,
    getPreviousStep,
    onCreateVerification,
    canCreateVerification = true
}) => {
    const isLastStep = activeStep === STEPS.PREVIEW;
    const selectionAdvancesImmediately = activeStep === STEPS.ENTRY_TYPE || activeStep === STEPS.TEMPLATE;

    // Same mechanism as the invoice editor's InvoiceStickyBar: the bar is
    // position: fixed so it is at the bottom of the viewport from the first
    // paint whatever the step's height, and an in-flow anchor both reserves
    // its height (so the last field is never hidden) and gives it the
    // column's left edge and width when the sidebar collapses or the window
    // resizes.
    const anchorRef = useRef(null);
    const barRef = useRef(null);
    const [frame, setFrame] = useState({ left: 0, width: 0, height: 0 });

    useLayoutEffect(() => {
        const measure = () => {
            const anchor = anchorRef.current;
            const bar = barRef.current;
            if (!anchor || !bar) return;
            const rect = anchor.getBoundingClientRect();
            setFrame({ left: rect.left, width: rect.width, height: bar.offsetHeight });
        };
        measure();
        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        if (observer && anchorRef.current) observer.observe(anchorRef.current);
        if (observer && barRef.current) observer.observe(barRef.current);
        window.addEventListener('resize', measure);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [activeStep]);

    if (selectionAdvancesImmediately && !canGoBack) return null;

    const placement = frame.width ? { left: frame.left, width: frame.width } : undefined;

    return (
        <>
            <style>{`
                /* Reserves the bar's height plus its offset from the edge, so
                   the last card clears the bar at full scroll. */
                .navigation-bar__anchor {
                    margin-top: 0.25rem;
                    margin-bottom: 2rem;
                }

                /* Fades the page out under the bar so the form does not show
                   through the gap between the bar and the viewport edge. */
                .navigation-bar__backdrop {
                    position: fixed;
                    bottom: 0;
                    z-index: 1019;
                    height: 7.5rem;
                    pointer-events: none;
                    background: linear-gradient(to bottom, rgba(237, 242, 238, 0) 0%, #edf2ee 45%);
                }

                .navigation-bar {
                    position: fixed;
                    bottom: 1.5rem;
                    z-index: 1020;
                    box-sizing: border-box;
                    background: #fff;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 0.75rem 1.25rem;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    box-shadow: 0 8px 24px rgba(17, 24, 39, 0.1), 0 1px 3px rgba(17, 24, 39, 0.06);
                }

                .nav-button {
                    border-radius: 8px;
                    padding: 0.6rem 1.25rem;
                    font-weight: 600;
                    font-size: 0.875rem;
                    border: none;
                    transition: all 0.2s ease;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    cursor: pointer;
                    min-width: 120px;
                    justify-content: center;
                }
                
                .btn-nav-secondary {
                    background: #f1f5f9;
                    color: #64748b;
                    border: 1px solid #e2e8f0;
                }
                
                .btn-nav-secondary:hover:not(:disabled) {
                    background: #e2e8f0;
                    color: #475569;
                    transform: translateY(-1px);
                }
                
                .btn-nav-primary {
                    background: var(--bs-primary);
                    color: white;
                }
                
                .btn-nav-primary:hover:not(:disabled) {
                    background: var(--bs-primary);
                    transform: translateY(-1px);
                }
                
                .btn-nav-success {
                    background: var(--bs-primary);
                    color: white;
                    box-shadow: 0 4px 12px var(--rgba-primary-3);
                }

                .btn-nav-success:hover:not(:disabled) {
                    background: var(--primary-hover);
                    transform: translateY(-1px);
                    box-shadow: 0 6px 16px var(--rgba-primary-4);
                }
                
                .nav-button:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                    transform: none;
                }
                
                .loading-spinner {
                    width: 16px;
                    height: 16px;
                    border: 2px solid rgba(255, 255, 255, 0.3);
                    border-top: 2px solid currentColor;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }
                
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                
                .nav-center {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                }
                
                .step-indicator {
                    font-size: 0.875rem;
                    color: #64748b;
                    background: #f8fafc;
                    padding: 0.5rem 1rem;
                    border-radius: 20px;
                    border: 1px solid #e2e8f0;
                }
                
                /* Mobile responsive */
                @media (max-width: 768px) {
                    /* A sticky bar must stay short on a phone: one row, the
                       progress tracker already says which step this is. */
                    .navigation-bar {
                        bottom: 0.75rem;
                        gap: 0.75rem;
                        padding: 0.75rem 1rem;
                        border-radius: 10px;
                    }

                    .navigation-bar__backdrop {
                        height: 5.5rem;
                    }

                    .navigation-bar > div:first-child,
                    .navigation-bar > div:last-child {
                        flex: 1;
                        display: flex;
                    }

                    .navigation-bar > div:last-child {
                        justify-content: flex-end;
                    }

                    .nav-button {
                        min-width: auto;
                        padding: 0.6rem 0.9rem;
                    }

                    .nav-center {
                        display: none;
                    }
                }
            `}</style>

            <div
                ref={anchorRef}
                className="navigation-bar__anchor"
                style={{ height: (frame.height || 64) + 24 }}
                aria-hidden="true"
            />
            <div className="navigation-bar__backdrop" aria-hidden="true" style={placement} />

            <div ref={barRef} className="navigation-bar" role="region" aria-label="Stegkontroller" style={placement}>
                <div>
                    {canGoBack ? (
                        <button
                            className="nav-button btn-nav-secondary"
                            onClick={() => onStepChange(getPreviousStep(), 'backward')}
                            disabled={operationInProgress}
                        >
                            <ArrowLeft size={16} />
                            Föregående
                        </button>
                    ) : (
                        <div style={{ width: '120px' }}></div>
                    )}
                </div>

                <div className="nav-center">
                    <div className="step-indicator">
                        Steg {activeStep === STEPS.ENTRY_TYPE ? '1' :
                            activeStep === STEPS.TEMPLATE ? '2' :
                                activeStep === STEPS.DETAILS ? '3' : '4'} av 4
                    </div>
                </div>

                <div>
                    {isLastStep ? (
                        <button
                            className="nav-button btn-nav-success"
                            onClick={onCreateVerification}
                            disabled={operationInProgress || !canCreateVerification}
                        >
                            {operationInProgress ? (
                                <>
                                    <div className="loading-spinner" />
                                    Skapar...
                                </>
                            ) : (
                                <>
                                    <Save size={16} />
                                    Skapa verifikat
                                </>
                            )}
                        </button>
                    ) : selectionAdvancesImmediately ? (
                        <div style={{ width: '120px' }} />
                    ) : (
                        <button
                            className="nav-button btn-nav-primary"
                            onClick={() => onStepChange(getNextStep(), 'forward')}
                            disabled={!canProceedToNext || operationInProgress}
                        >
                            Granska bokföring
                            <ArrowRight size={16} />
                        </button>
                    )}
                </div>
            </div>
        </>
    );
};

export default BookkeepingNavigationBar;
